import React, { useState, useEffect, useRef } from 'react';
import useLocale from 'hooks/useLocale';
import styles from './Form.module.scss';
import Button from 'components/global/button/Button';
import Dropdown from 'components/global/form/Dropdown';
import { useRouter } from 'next/router';
import Script from 'next/script';

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void | Promise<void>) => void;
      execute: (
        siteKey: string,
        options: { action: string }
      ) => Promise<string>;
    };
  }
}

const RECAPTCHA_SRC = `https://www.google.com/recaptcha/api.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}`;

const getRecaptchaToken = (): Promise<string> =>
  new Promise((resolve) => {
    if (!window.grecaptcha) {
      resolve('');
      return;
    }
    window.grecaptcha.ready(() => {
      window
        .grecaptcha!.execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!, {
          action: 'contact',
        })
        .then(resolve);
    });
  });

const withTimeout = <T,>(
  promise: Promise<T>,
  ms: number,
  fallback: T
): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout>;
  const timeout = new Promise<T>((resolve) => {
    timeoutId = setTimeout(() => resolve(fallback), ms);
  });
  return Promise.race([promise, timeout]).finally(() =>
    clearTimeout(timeoutId)
  );
};

const Form = () => {
  const [fullname, setFullname] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [mobile, setMobile] = useState('');
  const [company, setCompany] = useState('');
  const [inquiry, setInquiry] = useState('');
  const [hear, setHear] = useState('');
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [preferredContact, setPreferredContact] = useState('');
  const [message, setMessage] = useState('');

  const isSubmittingRef = useRef(false);
  const router = useRouter();
  const { lang } = useLocale();

  useEffect(() => {
    if (router.asPath.includes('rental-vehicles')) {
      setInquiry(lang.formInquiryRentingSWATTrucks);
    }
  }, [router.asPath]);

  useEffect(() => {
    if (router.query.source === 'become-a-dealer') {
      setInquiry(lang.formInquiryDealer);
    }
  }, [router.query.source]);

  const [isCompanyDropdownActive, setIsCompanyDropdownActive] = useState(false);
  const [isInquiryDropdownActive, setIsInquiryDropdownActive] = useState(false);
  const [
    isPreferredContactDropdownActive,
    setIsPreferredContactDropdownActive,
  ] = useState(false);
  const [isHearDropdownActive, setIsHearDropdownActive] = useState(false);
  const [isCountryDropdownActive, setIsCountryDropdownActive] = useState(false);
  const [isStateDropdownActive, setIsStateDropdownActive] = useState(false);

  const countryOptions = [
    'United States',
    'Afghanistan',
    'Albania',
    'Algeria',
    'Andorra',
    'Angola',
    'Antigua and Barbuda',
    'Argentina',
    'Armenia',
    'Australia',
    'Austria',
    'Azerbaijan',
    'Bahamas',
    'Bahrain',
    'Bangladesh',
    'Barbados',
    'Belarus',
    'Belgium',
    'Belize',
    'Benin',
    'Bhutan',
    'Bolivia',
    'Bosnia and Herzegovina',
    'Botswana',
    'Brazil',
    'Brunei',
    'Bulgaria',
    'Burkina Faso',
    'Burundi',
    'Cabo Verde',
    'Cambodia',
    'Cameroon',
    'Canada',
    'Central African Republic',
    'Chad',
    'Chile',
    'China',
    'Colombia',
    'Comoros',
    'Congo, Democratic Republic of',
    'Congo, Republic of',
    'Costa Rica',
    "Côte d'Ivoire",
    'Croatia',
    'Cuba',
    'Cyprus',
    'Czech Republic',
    'Denmark',
    'Djibouti',
    'Dominica',
    'Dominican Republic',
    'Ecuador',
    'Egypt',
    'El Salvador',
    'Equatorial Guinea',
    'Eritrea',
    'Estonia',
    'Eswatini',
    'Ethiopia',
    'Fiji',
    'Finland',
    'France',
    'Gabon',
    'Gambia',
    'Georgia',
    'Germany',
    'Ghana',
    'Greece',
    'Grenada',
    'Guatemala',
    'Guinea',
    'Guinea-Bissau',
    'Guyana',
    'Haiti',
    'Honduras',
    'Hungary',
    'Iceland',
    'India',
    'Indonesia',
    'Iran',
    'Iraq',
    'Ireland',
    'Israel',
    'Italy',
    'Jamaica',
    'Japan',
    'Jordan',
    'Kazakhstan',
    'Kenya',
    'Kiribati',
    'Kosovo',
    'Kuwait',
    'Kyrgyzstan',
    'Laos',
    'Latvia',
    'Lebanon',
    'Lesotho',
    'Liberia',
    'Libya',
    'Liechtenstein',
    'Lithuania',
    'Luxembourg',
    'Madagascar',
    'Malawi',
    'Malaysia',
    'Maldives',
    'Mali',
    'Malta',
    'Marshall Islands',
    'Mauritania',
    'Mauritius',
    'Mexico',
    'Micronesia',
    'Moldova',
    'Monaco',
    'Mongolia',
    'Montenegro',
    'Morocco',
    'Mozambique',
    'Myanmar',
    'Namibia',
    'Nauru',
    'Nepal',
    'Netherlands',
    'New Zealand',
    'Nicaragua',
    'Niger',
    'Nigeria',
    'North Macedonia',
    'Norway',
    'Oman',
    'Pakistan',
    'Palau',
    'Panama',
    'Papua New Guinea',
    'Paraguay',
    'Peru',
    'Philippines',
    'Poland',
    'Portugal',
    'Qatar',
    'Romania',
    'Russia',
    'Rwanda',
    'Saint Kitts and Nevis',
    'Saint Lucia',
    'Saint Vincent and the Grenadines',
    'Samoa',
    'San Marino',
    'Sao Tome and Principe',
    'Saudi Arabia',
    'Senegal',
    'Serbia',
    'Seychelles',
    'Sierra Leone',
    'Singapore',
    'Slovakia',
    'Slovenia',
    'Solomon Islands',
    'Somalia',
    'South Africa',
    'Spain',
    'Sri Lanka',
    'Sudan',
    'Suriname',
    'Sweden',
    'Switzerland',
    'Syria',
    'Taiwan',
    'Tajikistan',
    'Tanzania',
    'Thailand',
    'Timor-Leste',
    'Togo',
    'Tonga',
    'Trinidad and Tobago',
    'Tunisia',
    'Turkey',
    'Turkmenistan',
    'Tuvalu',
    'Uganda',
    'Ukraine',
    'United Arab Emirates',
    'United Kingdom',
    'Uruguay',
    'Uzbekistan',
    'Vanuatu',
    'Venezuela',
    'Vietnam',
    'Yemen',
    'Zambia',
    'Zimbabwe',
  ];

  const stateOptions = [
    'Alabama',
    'Alaska',
    'Arizona',
    'Arkansas',
    'California',
    'Colorado',
    'Connecticut',
    'Delaware',
    'Florida',
    'Georgia',
    'Hawaii',
    'Idaho',
    'Illinois',
    'Indiana',
    'Iowa',
    'Kansas',
    'Kentucky',
    'Louisiana',
    'Maine',
    'Maryland',
    'Massachusetts',
    'Michigan',
    'Minnesota',
    'Mississippi',
    'Missouri',
    'Montana',
    'Nebraska',
    'Nevada',
    'New Hampshire',
    'New Jersey',
    'New Mexico',
    'New York',
    'North Carolina',
    'North Dakota',
    'Ohio',
    'Oklahoma',
    'Oregon',
    'Pennsylvania',
    'Rhode Island',
    'South Carolina',
    'South Dakota',
    'Tennessee',
    'Texas',
    'Utah',
    'Vermont',
    'Virginia',
    'Washington',
    'West Virginia',
    'Wisconsin',
    'Wyoming',
  ];

  const [errors, setErrors] = useState<Record<string, string>>({});

  function sanitizeInput(input) {
    return input.replace(/[&<>"'/\w\s]/g, function (char) {
      switch (char) {
        case '&':
          return '&amp;';
        case '<':
          return '&lt;';
        case '>':
          return '&gt;';
        case '"':
          return '&quot;';
        case "'":
          return '&#39;';
        case '/':
          return '&#x2F;';
        default:
          return char;
      }
    });
  }

  const validateFullname = (value) => {
    const fullNamePattern = /^[A-Z ]{3,}$/i;
    if (!value) {
      return lang.formErrorsNameRequired;
    } else if (!fullNamePattern.test(value)) {
      return lang.formErrorsNameValid;
    } else {
      return '';
    }
  };

  const validateEmail = (value) => {
    const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!value) {
      return lang.formErrorsEmailRequired;
    } else if (!emailPattern.test(value)) {
      return lang.formErrorsEmailValid;
    } else {
      return '';
    }
  };

  const validatePhone = (value) => {
    const phonePattern = /^\D?(\d{3})\D?\D?(\d{3})\D?(\d{4,6})$/;
    if (value && !phonePattern.test(value)) {
      return lang.formErrorsPhoneValid;
    } else {
      return '';
    }
  };

  const validateMobile = (value) => {
    const mobilePattern = /^\D?(\d{3})\D?\D?(\d{3})\D?(\d{4,6})$/;
    if (!value) {
      return lang.formErrorsMobileRequired;
    } else if (!mobilePattern.test(value)) {
      return lang.formErrorsMobileValid;
    } else {
      return '';
    }
  };

  const validateCountry = (value) => {
    if (!value) {
      return lang.formErrorsCountryRequired;
    } else {
      return '';
    }
  };

  const validateState = (value) => {
    if (country === 'United States' && !value) {
      return lang.formErrorsStateRequired;
    } else {
      return '';
    }
  };

  const handleFieldChange = (field, value, validator, setter) => {
    setter(value);
    const errorMessage = validator(value);
    setErrors({ ...errors, [field]: errorMessage });
  };

  const headers = {
    'Content-Type': 'application/json',
  };

  const handleSubmit = async () => {
    const newErrors = {
      fullname: validateFullname(fullname),
      email: validateEmail(email),
      phone: validatePhone(phone),
      mobile: validateMobile(mobile),
      country: validateCountry(country),
      state: validateState(state), // Validate state if the country is United States
    };

    setErrors(newErrors);

    const hasError = Object.values(newErrors).some((error) => error !== '');

    if (!hasError) {
      if (isSubmittingRef.current) return;
      isSubmittingRef.current = true;

      const sanitizedMessage = sanitizeInput(message);

      const submitBtn = document.querySelector('.submitButton');
      submitBtn.classList.add('submiting');
      submitBtn.innerHTML = '';

      try {
        const token = await withTimeout(getRecaptchaToken(), 5000, '');

        const response = await fetch(`/api/contact`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            token,
            data: {
              name: fullname,
              email: email,
              mobileNumber: mobile,
              phoneNumber: phone,
              company: company,
              inquiry: inquiry,
              preferredContact: preferredContact,
              hear: hear,
              country: country,
              state: country === 'United States' ? state : '',
              message: sanitizedMessage,
              route: window.location.origin + router.asPath,
              date: Date.now(),
              domain: 'swats',
            },
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to submit form');
        }

        // Handle success
        submitBtn.classList.remove('submiting');
        submitBtn.innerHTML = lang.formSent;
        submitBtn.classList.add('submitted');

        // GTM conversion tracking
        try {
          const pathname = window.location.pathname;
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'generate_lead',
            form_name: pathname.includes('/contact') ? 'contact' : 'rfq',
            form_location: pathname,
            site: 'armored-swat.com',
          });
        } catch {
          // no-op — analytics must never break the success UX
        }

        // Reset form fields
        setFullname('');
        setEmail('');
        setPhone('');
        setMobile('');
        setCompany('');
        setInquiry('');
        setHear('');
        setCountry('');
        setState('');
        setPreferredContact('');
        setMessage('');

        // Show success message to user
      } catch (error) {
        // console.error('Error:', error);
        // Handle error
        submitBtn.classList.remove('submiting');
        submitBtn.innerHTML = lang.error;
        submitBtn.classList.add('error');
      } finally {
        isSubmittingRef.current = false;
      }

      setTimeout(() => {
        submitBtn.innerHTML = lang.formSendMessage;
        submitBtn.classList.remove('submitted', 'error');
      }, 2000);
    }
  };

  return (
    <div className={`${styles.form}`}>
      <Script src={RECAPTCHA_SRC} strategy="afterInteractive" />

      <div
        className={`${styles.form_group} ${
          errors.fullname ? styles.error : ''
        }`}
      >
        <input
          type="text"
          id="fullname"
          value={fullname}
          onChange={(e) =>
            handleFieldChange(
              'fullname',
              e.target.value,
              validateFullname,
              setFullname
            )
          }
          placeholder={`${lang.formFullName}*`}
          // required
          className={`${styles.form_input}`}
        />
        <small className={`${styles.form_input_error}`}>
          {errors.fullname}
        </small>
      </div>

      <div
        className={`${styles.form_group} ${errors.email ? styles.error : ''}`}
      >
        <input
          type="email"
          id="email"
          value={email}
          onChange={(e) =>
            handleFieldChange('email', e.target.value, validateEmail, setEmail)
          }
          placeholder={`${lang.formEmail}*`}
          className={`${styles.form_input}`}
          // required
        />
        <small className={`${styles.form_input_error}`}>{errors.email}</small>
      </div>

      <div
        className={`${styles.form_group} ${errors.mobile ? styles.error : ''}`}
      >
        <input
          type="tel"
          id="mobile"
          value={mobile}
          onChange={(e) =>
            handleFieldChange(
              'mobile',
              e.target.value,
              validateMobile,
              setMobile
            )
          }
          placeholder={`${lang.formMobileNumber}*`}
          className={`${styles.form_input}`}
          // required
        />
        <small className={`${styles.form_input_error}`}>{errors.mobile}</small>
      </div>

      <div
        className={`${styles.form_group} ${errors.phone ? styles.error : ''}`}
      >
        <input
          type="tel"
          id="phone"
          value={phone}
          onChange={(e) =>
            handleFieldChange('phone', e.target.value, validatePhone, setPhone)
          }
          placeholder={`${lang.formAlternatePhoneNumber}`}
          className={`${styles.form_input}`}
        />
        <small className={`${styles.form_input_error}`}>{errors.phone}</small>
      </div>

      <div
        className={`${styles.form_group} ${errors.company ? styles.error : ''}`}
      >
        <Dropdown
          label={`${lang.formCustomerType}`}
          options={[
            {
              label: lang.formCustomerTypeUSGovernment,
              subOptions: [
                lang.formCustomerTypeUSMilitary,
                lang.formCustomerTypeFederalGovernment,
                lang.formCustomerTypeStateGovernment,
                lang.formCustomerTypeCountyGovernment,
                lang.formCustomerTypeCityGovernment,
                lang.formCustomerTypeSheriffs,
              ],
            },
            {
              label: lang.formCustomerTypeForeignGovernment,
              subOptions: [
                lang.formCustomerTypeForeignGovernmentMilitary,
                lang.formCustomerTypeForeignGovernmentNonMilitary,
              ],
            },
            lang.formCustomerTypeBusiness,
            {
              label: lang.formCustomerTypePrivateIndividual,
              subOptions: [
                lang.formCustomerTypeUSResident,
                lang.formCustomerTypeNonUSResident,
              ],
            },
            lang.formCustomerTypeBroker,
            lang.formCustomerTypeNGO,
            lang.formCustomerTypeOther,
          ]}
          selectedOption={company}
          setSelectedOption={setCompany}
          isActive={isCompanyDropdownActive}
          setIsActive={setIsCompanyDropdownActive}
        />
        <small className={`${styles.form_input_error}`}>{errors.company}</small>
      </div>

      <div
        className={`${styles.form_group} ${errors.inquiry ? styles.error : ''}`}
      >
        <Dropdown
          label={`${lang.formInquiry}`}
          options={[
            {
              label: lang.formInquiryPoliceLawEnforcement,
              subOptions: [
                lang.formInquirySWAT,
                lang.formInquiryTacticalVans,
                lang.formInquiryAllOfTheAbove,
                lang.formInquiryRiot,
                lang.formInquiryOther,
              ],
            },
            lang.formInquiryRentingSWATTrucks,
            lang.formInquiryPreviouslyOwned,
            lang.formInquiryParts,
            lang.formInquiryWarranty,
            lang.formInquiryDealer,
            lang.formInquiryEmployment,
            lang.formInquiryOther,
          ]}
          selectedOption={inquiry}
          setSelectedOption={setInquiry}
          isActive={isInquiryDropdownActive}
          setIsActive={setIsInquiryDropdownActive}
        />
        <small className={`${styles.form_input_error}`}>{errors.inquiry}</small>
      </div>

      <div
        className={`${styles.form_group} ${
          errors.preferredContact ? styles.error : ''
        }`}
      >
        <Dropdown
          label={`${lang.formContactVia}`}
          options={[
            lang.formContactViaMobile,
            lang.formContactViaLandline,
            lang.formContactViaEmail,
            lang.formContactViaText,
            lang.formContactViaWhatsapp,
          ]}
          selectedOption={preferredContact}
          setSelectedOption={setPreferredContact}
          isActive={isPreferredContactDropdownActive}
          setIsActive={setIsPreferredContactDropdownActive}
        />
        <small className={`${styles.form_input_error}`}>
          {errors.preferredContact}
        </small>
      </div>

      <div
        className={`${styles.form_group} ${errors.hear ? styles.error : ''}`}
      >
        <Dropdown
          label={`${lang.formHear}`}
          options={[
            {
              label: lang.formHearSocialMedia,
              subOptions: [
                lang.formHearYouTube,
                lang.formHearInstagram,
                lang.formHearTikTok,
                lang.formHearFacebook,
                lang.formHearTwitter,
                lang.formHearLinkedIn,
                lang.formHearWeChat,
                lang.formHearSnapchat,
                lang.formHearReddit,
                lang.formHearOther,
              ],
            },
            {
              label: lang.formHearSearchEngines,
              subOptions: [
                lang.formHearGoogle,
                lang.formHearBing,
                lang.formHearYahoo,
                lang.formHearDuckDuckGo,
                lang.formHearBaidu,
                lang.formHearOther,
              ],
            },
            {
              label: lang.formHearAI,
              subOptions: [
                lang.formHearGemini,
                lang.formHearChatGPT,
                lang.formHearClaude,
                lang.formHearCoPilot,
                lang.formHearGrok,
                lang.formHearPerplexity,
                lang.formHearMetaAI,
                lang.formHearMistral,
                lang.formHearDeepSeek,
              ],
            },
            lang.formHearTradeShow,
            lang.formHearReferral,
            lang.formHearRepeat,
            lang.formHearThird,
            lang.formHearKnew,
            lang.formHearArmoredVehiclesSite,
            lang.formHearFriend,
            lang.formHearOther,
          ]}
          selectedOption={hear}
          setSelectedOption={setHear}
          isActive={isHearDropdownActive}
          setIsActive={setIsHearDropdownActive}
        />
        <small className={`${styles.form_input_error}`}>{errors.hear}</small>
      </div>

      <div
        className={`${styles.form_group} ${errors.country ? styles.error : ''}`}
      >
        <Dropdown
          label={`${lang.formCountry}*`}
          options={countryOptions}
          selectedOption={country}
          setSelectedOption={(value) => {
            setCountry(value);
            setState(''); // Reset state if country changes
          }}
          isActive={isCountryDropdownActive}
          setIsActive={setIsCountryDropdownActive}
        />
        <small className={`${styles.form_input_error}`}>{errors.country}</small>
      </div>

      <div
        className={`${styles.form_group} ${errors.state ? styles.error : ''} ${
          country !== 'United States' ? styles.disabled : ''
        }`}
      >
        <Dropdown
          label={`${lang.formState}*`}
          options={stateOptions}
          selectedOption={state}
          setSelectedOption={setState}
          isActive={country === 'United States' ? isStateDropdownActive : false}
          setIsActive={setIsStateDropdownActive}
        />
        <small className={styles.form_input_error}>{errors.state}</small>
      </div>

      <div
        className={`${styles.form_group} ${styles.form_group_full} ${
          errors.message ? styles.error : ''
        }`}
      >
        <textarea
          placeholder={`${lang.formMessage}`}
          id="message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={7}
          className={`${styles.form_input} ${styles.form_textarea}`}
        />
        <small className={`${styles.form_input_error}`}>{errors.message}</small>
      </div>

      <div className={`${styles.form_submit} center`}>
        <Button
          button={true}
          onClick={handleSubmit}
          className={`${styles.form_submit_button} submitButton primary rounded`}
        >
          {lang.formSendMessage}
        </Button>
      </div>
    </div>
  );
};

export default Form;
